import { useState } from 'react';
import { Col, Form, message, Row } from 'antd';
import { CustomButton, CustomInput } from '../../components';
import { authenticatePassword } from '../../apis/shipment';
import { useSpin } from '../../components/SpinContext';
import LoginLogo from '../../assets/images/auth/zoomlogi-logo.png';
import CustomParagraph from '../../components/CustomParagraph';
import theme from '../../assets/theme';
import { getThemeAccentColor } from '@/utils/themeColor';

interface FormValues {
  password: string;
}

interface AuthenticateShareShipmentProps {
  uuid: string;
  setPasswordRequired: (required: boolean) => void;
  setProductDetails: (details: boolean) => void;
  setShowAlerts: (show: boolean) => void;
}

/**
 * AuthenticateShareShipment Component
 *
 * Password authentication form for accessing shared shipment details.
 * Validates password input, calls authentication API, and manages
 * access control for shared shipment viewing with proper error handling.
 */
const AuthenticateShareShipment = ({
  uuid,
  setPasswordRequired,
  setProductDetails,
  setShowAlerts,
}: AuthenticateShareShipmentProps) => {
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const { hideSpin } = useSpin();

  const isZoomLogi = theme?.themeType === 1;
  const buttonBgColor = getThemeAccentColor();
  const logoSrc = isZoomLogi ? LoginLogo : theme?.headerLogo;
  const logoWidth =
    (isZoomLogi
      ? '100px'
      : theme?.loginLogoWidth ||
        theme?.authHeaderLogoWidth ||
        theme?.headerLogoWidth) || '150px';
  const logoMarginRight = theme?.loginLogoMarginRight || '0px';

  const onFinish = async (values: FormValues): Promise<void> => {
    if (values.password.length >= 6) {
      setIsFormValid(true);
      const payload = {
        password: values.password,
        token: uuid,
      };
      authenticatePassword(payload)
        .then((res) => {
          const data = res?.data || null;
          if (data) {
            if (data?.data?.is_success) {
              setProductDetails(data?.data?.data?.show_product_details);
              setShowAlerts(data?.data?.data?.show_alerts);
              setPasswordRequired(false);
              form.resetFields();
            } else {
              message.error(data?.data?.message);
            }
          }
        })

        .finally(() => {
          hideSpin();
          setIsFormValid(false);
        });
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        flexDirection: 'column',
      }}
    >
      <img
        src={logoSrc}
        alt={theme?.title || 'Logo'}
        style={{
          width: logoWidth,
          marginBottom: '20px',
          marginRight: logoMarginRight,
        }}
      />
      {isZoomLogi && (
        <CustomParagraph
          text='Zoomlogi'
          fontSize={'27px'}
          fontWeight={'600'}
          lineHeight={'49px'}
          marginBottom={0}
        />
      )}
      <Form
        layout='vertical'
        form={form}
        name='validateOnly'
        onFinish={onFinish}
        style={{ maxWidth: '300px', margin: '0 auto', textAlign: 'center' }}
      >
        <Row gutter={[16, 24]}>
          <Col span={24}>
            <Form.Item
              className='custom-input'
              label='Password'
              name='password'
              style={{ textAlign: 'left' }}
              rules={[
                { required: true, message: 'Please enter your password' },
                {
                  type: 'string',
                  min: 6,
                  message: 'Password must be at least 6 characters long.',
                },
              ]}
            >
              <CustomInput
                placeholder='Enter password'
                height='36px'
                border='0px'
                bgColor='var(--light-gray)'
                textColor='var(--text-secondary)'
                borderRadius='8px'
                placeholderColor='var(--text-secondary)'
                lineHeight='38px'
                type='password'
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <CustomButton
              text='Authenticate'
              block={true}
              height='36px'
              bgColor={buttonBgColor}
              color='#fff'
              borderRadius='8px'
              fontSize='14px'
              fontWeight={500}
              isFormValid={isFormValid}
              htmlType='submit'
            />
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default AuthenticateShareShipment;
